import React, { useEffect, useState } from "react";
import ModalBackdrop from "components/ModalBackdrop";
import { NumberInput, TextAreaInput, TextInput } from "components/InputStyled";

const OBJECT_TYPES = [
  { object_type: "project", label: "Projects" },
  { object_type: "debt_allocation_component", label: "Debt allocation components" },
];

export default function ClassificationModal({ classification, saving, onSave, onDelete, onClose }) {
  const [draft, setDraft] = useState({ name: "", code: "", description: "", sort_order: 0, is_active: true });
  const [scopes, setScopes] = useState([]);

  useEffect(() => {
    setDraft({ name: "", code: "", description: "", sort_order: 0, is_active: true, ...(classification || {}) });
    setScopes(
      OBJECT_TYPES.map((type) => {
        const existing = classification?.scopes?.find((scope) => scope.object_type === type.object_type);
        return {
          ...type,
          enabled: !!existing,
          is_required: !!existing?.is_required,
          allow_multiple: !!existing?.allow_multiple,
        };
      })
    );
  }, [classification]);

  const updateScope = (objectType, field, checked) =>
    setScopes((current) => current.map((scope) => {
      if (scope.object_type === objectType) return { ...scope, [field]: checked };
      if (
        objectType === "debt_allocation_component" &&
        field === "enabled" &&
        checked &&
        scope.object_type === "project"
      ) {
        return { ...scope, enabled: true };
      }
      return scope;
    }));

  return (
    <ModalBackdrop onClose={saving ? undefined : onClose}>
      <form
        className="w-full max-w-2xl rounded-xl border border-border bg-app p-6 shadow-xl"
        onSubmit={(event) => { event.preventDefault(); onSave(draft, scopes); }}
      >
        <h2 className="text-lg font-semibold text-brand">{classification?.id ? "Edit Classification" : "New Classification"}</h2>
        <p className="mt-1 text-sm text-muted">Names such as Business Unit and Repayment Source are organization-configured.</p>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <TextInput label="Name" value={draft.name} onChange={(name) => setDraft((value) => ({ ...value, name }))} className="w-full" required />
          <TextInput label="Code" value={draft.code} onChange={(code) => setDraft((value) => ({ ...value, code }))} className="w-full" />
          <NumberInput label="Sort Order" value={draft.sort_order} onChange={(sort_order) => setDraft((value) => ({ ...value, sort_order }))} type="integer" className="w-full" />
        </div>
        <div className="mt-4"><TextAreaInput label="Description" value={draft.description} onChange={(description) => setDraft((value) => ({ ...value, description }))} rows={3} /></div>

        <div className="mt-5 rounded-lg border border-border p-4">
          <div className="font-medium text-app">Assignment rules</div>
          <div className="mt-3 space-y-3">
            {scopes.map((scope) => (
              <div key={scope.object_type} className="grid items-center gap-3 sm:grid-cols-[1fr_auto_auto]">
                <label className="flex items-center gap-2 text-sm text-app">
                  <input type="checkbox" checked={scope.enabled} onChange={(event) => updateScope(scope.object_type, "enabled", event.target.checked)} />
                  {scope.label}
                </label>
                <label className="flex items-center gap-2 text-sm text-muted">
                  <input type="checkbox" checked={scope.is_required} disabled={!scope.enabled} onChange={(event) => updateScope(scope.object_type, "is_required", event.target.checked)} /> Required
                </label>
                <label className="flex items-center gap-2 text-sm text-muted">
                  {scope.object_type === "debt_allocation_component" ? (
                    <span>One value per component</span>
                  ) : (
                    <>
                      <input type="checkbox" checked={scope.allow_multiple} disabled={!scope.enabled} onChange={(event) => updateScope(scope.object_type, "allow_multiple", event.target.checked)} /> Multiple values
                    </>
                  )}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-2">
          <div>
            {classification?.id ? (
              <button type="button" className="btn bg-red-600 text-white hover:bg-red-700" onClick={onDelete} disabled={saving}>
                Delete Classification
              </button>
            ) : null}
          </div>
          <div className="flex gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving || !draft.name.trim()}>{saving ? "Saving…" : "Save Classification"}</button>
          </div>
        </div>
      </form>
    </ModalBackdrop>
  );
}
